import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useLegacyAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export const CreateInvoiceCommentSchema = z.object({
  id: z.union([z.string(), z.number()]),
  comment: z.string(),
});

export type CreateInvoiceCommentVariables = z.infer<typeof CreateInvoiceCommentSchema>;

export async function createInvoiceCommentFn(
  axiosInstance: Promise<AxiosInstance>,
  data: CreateInvoiceCommentVariables,
) {
  const instance = await axiosInstance;

  return instance.post(`invoices/${data.id}/comments`, CreateInvoiceCommentSchema.parse(data));
}

export function useCreateInvoiceComment() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, CreateInvoiceCommentVariables>({
    mutationFn: data => createInvoiceCommentFn(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['comments'],
      });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    CreateInvoiceCommentVariables,
    'createInvoiceComment'
  >('createInvoiceComment', mutation);
}
